import Delete from '@mui/icons-material/Delete'
import Edit from '@mui/icons-material/Edit'
import { Box, Stack, IconButton, Typography } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import { useDeleteCustomVariable } from '@/service-library/hooks/custom-variables'
import queryKeys from '@/service-library/query-keys'
import {
  CustomVariable,
  CustomVariableCategory,
} from '@/types/custom-variables'
import { showErrorSnackbar } from '@/utils/snackbars'
import PixyMarkdown from '@/components/markdown/PixyMarkdown'
import AddEditCustomVariableDialog from './AddEditCustomVariableDialog'

type CustomVariableManagementRowProps = {
  variable: CustomVariable
  variablesQueryKey: QueryKey
  category: CustomVariableCategory & { variables: CustomVariable[] }
  categories: CustomVariableCategory[]
}

export default function CustomVariableManagementRow({
  variable,
  variablesQueryKey,
  category,
  categories,
}: CustomVariableManagementRowProps) {
  const editCustomVariableOverlay = useOverlay()

  const { deleteCustomVariable } = useDeleteCustomVariable({
    listQueryKey: variablesQueryKey,
    // needed since we use the root org to get the custom variables for the other tab
    sideEffectQueryKeys: [queryKeys.organizations.details()],
    onError: () => {
      showErrorSnackbar('Unable to delete variable. Please try again later.')
    },
  })

  return (
    <>
      <Box>
        <Stack spacing={1} direction="row">
          <IconButton onClick={editCustomVariableOverlay.open} size="small">
            <Edit color="primary" fontSize="inherit" />
          </IconButton>
          <IconButton
            onClick={() => {
              deleteCustomVariable(variable.id)
            }}
            size="small"
          >
            <Delete color="error" fontSize="inherit" />
          </IconButton>

          <AddEditCustomVariableDialog
            overlay={editCustomVariableOverlay}
            categories={categories}
            variable={variable}
            listQueryKey={variablesQueryKey}
            selectedCategory={category}
          />
        </Stack>
      </Box>

      <Box>
        <Typography sx={{ overflowWrap: 'break-word' }} variant="body2">
          {variable.name}
        </Typography>
      </Box>

      <Box>
        <PixyMarkdown
          components={{
            p: ({ children }) => (
              <Typography variant="body2" color="text.secondary">
                {children}
              </Typography>
            ),
          }}
        >
          {variable.description || ''}
        </PixyMarkdown>
      </Box>
    </>
  )
}
